/**
 * Runtime migration: GroupByVariable → AdHocFiltersVariable groupBy extension.
 *
 * GroupByVariable entries are merged into the matching AdHocFiltersVariable
 * (same datasource) as groupBy-operator filters. The GroupByVariable is then
 * dropped from the variable list so it is never instantiated.
 *
 * Since this is a runtime layer the schema will not be persisted until the user saves the dashboard.
 *
 * This is a temporary compat layer — remove once GroupByVariable is fully deprecated.
 */
import type { GroupByVariableModel, TypedVariableModel } from '@grafana/data';
import { locationService } from '@grafana/runtime';
import type {
  AdhocVariableKind,
  GroupByVariableKind,
  VariableKind,
} from '@grafana/schema/apis/dashboard.grafana.app/v2';

const GROUP_BY_OPERATOR = 'groupBy';

interface GroupByFilterEntry {
  key: string;
  keyLabel?: string;
  operator: typeof GROUP_BY_OPERATOR;
  value: '';
  condition: '';
  origin?: 'dashboard';
}

function toGroupByFilter(key: string, keyLabel?: string, origin?: 'dashboard'): GroupByFilterEntry {
  return {
    key,
    ...(keyLabel && keyLabel !== key ? { keyLabel } : {}),
    operator: GROUP_BY_OPERATOR,
    value: '',
    condition: '',
    ...(origin ? { origin } : {}),
  };
}

interface KeyWithLabel {
  key: string;
  label?: string;
}

function buildGroupByFilters(currentEntries: KeyWithLabel[], defaultEntries: KeyWithLabel[]): GroupByFilterEntry[] {
  const filters: GroupByFilterEntry[] = [];
  const seen = new Set<string>();

  for (const entry of defaultEntries) {
    seen.add(entry.key);
    filters.push(toGroupByFilter(entry.key, entry.label, 'dashboard'));
  }

  for (const entry of currentEntries) {
    if (!seen.has(entry.key)) {
      filters.push(toGroupByFilter(entry.key, entry.label));
    }
  }

  return filters;
}

function readGroupByUrlEntries(name: string): KeyWithLabel[] | undefined {
  const values = locationService
    .getSearch()
    .getAll(`var-${name}`)
    .filter((v) => v !== '');
  if (values.length === 0) {
    return undefined;
  }
  return values.map((key) => ({ key }));
}

/**
 * Rewrites the URL so that groupBy values are
 * folded into its matching filter as adhoc-style groupBy filter entries. This
 * runs before the scene activates, so scenes' URL sync sees the merged state
 * when it first reads the URL.
 */
function rewriteUrlForGroupByMigration(groupByName: string, adhocName: string): void {
  const search = locationService.getSearch();
  const groupByValues = search.getAll(`var-${groupByName}`);

  if (groupByValues.length === 0) {
    return;
  }

  const adhocEntries = groupByValues.filter((v) => v !== '').map((key) => `${key}|groupBy`);

  const groupByKey = `var-${groupByName}`;
  const adhocKey = `var-${adhocName}`;

  const existingAdhoc = search.getAll(adhocKey).filter((v) => v !== '');
  const merged = [...existingAdhoc, ...adhocEntries];

  locationService.partial(
    {
      [adhocKey]: merged.length === 0 ? '' : merged.length === 1 ? merged[0] : merged,
      [groupByKey]: null,
    },
    true
  );
}

// ---------------------------------------------------------------------------
// V1 (legacy JSON model)
// ---------------------------------------------------------------------------

/**
 * Migrates v1 `TypedVariableModel[]`: merges each GroupByVariable into its
 * datasource-matching AdHocFiltersVariable, then removes it.
 * Returns a new list (no GroupByVariables). Does not mutate the input.
 */
export function migrateGroupByVariablesV1(variables: TypedVariableModel[]): TypedVariableModel[] {
  const groupByVars = variables.filter(isGroupByModel);
  if (groupByVars.length === 0) {
    return variables;
  }

  const groupByByDsUid = new Map<string | undefined, GroupByVariableModel>();
  for (const gb of groupByVars) {
    groupByByDsUid.set(gb.datasource?.uid, gb);
  }

  const result: TypedVariableModel[] = [];

  for (const v of variables) {
    if (v.type === 'groupby') {
      continue;
    }

    if (v.type !== 'adhoc') {
      result.push(v);
      continue;
    }

    const gb = groupByByDsUid.get(v.datasource?.uid);
    if (!gb) {
      result.push(v);
      continue;
    }

    const urlEntries = readGroupByUrlEntries(gb.name);
    const currentEntries = urlEntries ?? extractV1Entries(gb.current);
    const defaultEntries = extractV1Entries(gb.defaultValue);
    const groupByFilters = buildGroupByFilters(currentEntries, defaultEntries);

    if (urlEntries && urlEntries.length > 0) {
      rewriteUrlForGroupByMigration(gb.name, v.name);
    }

    result.push({
      ...v,
      enableGroupBy: true,
      filters: [...(v.filters ?? []), ...groupByFilters],
    });
  }

  return result;
}

function isGroupByModel(v: TypedVariableModel): v is GroupByVariableModel {
  return v.type === 'groupby';
}

function extractV1Entries(option?: { value?: string | string[]; text?: string | string[] }): KeyWithLabel[] {
  if (!option?.value) {
    return [];
  }

  const values = Array.isArray(option.value) ? option.value.map(String) : [String(option.value)];
  const labels = Array.isArray(option.text) ? option.text.map(String) : option.text ? [String(option.text)] : [];

  return values.filter(Boolean).map((val, i) => ({
    key: val,
    label: labels[i] || undefined,
  }));
}

// ---------------------------------------------------------------------------
// V2 (schema v2 kinds)
// ---------------------------------------------------------------------------

/**
 * Migrates v2 `VariableKind[]`: merges each GroupByVariableKind into its
 * datasource-matching AdhocVariableKind, then removes it.
 * Returns a new list (no GroupByVariableKinds). Does not mutate the input.
 *
 * With `migrateUrlState: false` only the saved state is migrated: the URL is
 * neither read nor rewritten.
 */
export function migrateGroupByVariablesV2(
  variables: VariableKind[],
  { migrateUrlState = true }: { migrateUrlState?: boolean } = {}
): VariableKind[] {
  const groupByVars = variables.filter((v): v is GroupByVariableKind => v.kind === 'GroupByVariable');
  if (groupByVars.length === 0) {
    return variables;
  }

  const groupByByDsName = new Map<string | undefined, GroupByVariableKind>();
  for (const gb of groupByVars) {
    groupByByDsName.set(gb.datasource?.name, gb);
  }

  const result: VariableKind[] = [];

  for (const v of variables) {
    if (v.kind === 'GroupByVariable') {
      continue;
    }

    if (v.kind !== 'AdhocVariable') {
      result.push(v);
      continue;
    }

    const gb = groupByByDsName.get(v.datasource?.name);
    if (!gb) {
      result.push(v);
      continue;
    }

    const urlEntries = migrateUrlState ? readGroupByUrlEntries(gb.spec.name) : undefined;
    const currentEntries = urlEntries ?? extractV2Entries(gb.spec.current);
    const defaultEntries = extractV2Entries(gb.spec.defaultValue);
    const groupByFilters = buildGroupByFilters(currentEntries, defaultEntries);

    if (urlEntries && urlEntries.length > 0) {
      rewriteUrlForGroupByMigration(gb.spec.name, v.spec.name);
    }

    const migrated: AdhocVariableKind = {
      ...v,
      spec: {
        ...v.spec,
        enableGroupBy: true,
        filters: [...(v.spec.filters ?? []), ...groupByFilters],
      },
    };
    result.push(migrated);
  }

  return result;
}

function extractV2Entries(option?: { value?: string | string[]; text?: string | string[] }): KeyWithLabel[] {
  if (!option?.value) {
    return [];
  }

  const values = Array.isArray(option.value) ? option.value.map(String) : [String(option.value)];
  const labels = Array.isArray(option.text) ? option.text.map(String) : option.text ? [String(option.text)] : [];

  return values.filter(Boolean).map((val, i) => ({
    key: val,
    label: labels[i] || undefined,
  }));
}
